/**
 * Route guard.
 *
 * Hiding a nav item stops people looking for a page; it does not stop them
 * typing its URL. Without this, /users is one address bar away from a red
 * "Admin role required" box — which is where the product was before.
 *
 * Redirects rather than rendering an error, because a page a role can never
 * open is not an error state, it is somewhere they do not go. The API refuses
 * it regardless; this is about not showing someone a door that will not open.
 */
import React from 'react';
import { Box, CircularProgress } from '@mui/material';
import { Navigate } from 'react-router-dom';

import { Capability, usePermissions } from './PermissionsProvider';

export default function RequireCapability({
  capability,
  children,
  redirectTo = '/copilot',
}: {
  capability: Capability;
  children: React.ReactNode;
  /** Somewhere every role can go. */
  redirectTo?: string;
}) {
  const { can, loading } = usePermissions();

  // Deciding before the capabilities have arrived would bounce every user off
  // every guarded page on a cold load.
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!can(capability)) {
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
}
